import React from 'react';
import { Transition } from '@headlessui/react';
import {
  CheckCircleIcon,
  XCircleIcon,
  InformationCircleIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { XMarkIcon as SolidXMarkIcon } from '@heroicons/react/20/solid';
import { toast, Toast } from 'react-hot-toast'; // Import the Toast type for 't'

// Define the props interface for this component
export interface NotificationToastProps { 
  t: Toast;
  type?: 'success' | 'error' | 'info' | 'warning';
  title: string;
  message?: string;
}

const NotificationToast: React.FC<NotificationToastProps> = ({
  t,
  type = 'info',
  title,
  message,
}) => {
  const renderIcon = () => {
    switch (type) {
      case 'success':
        return (
          <CheckCircleIcon
            aria-hidden="true"
            className="size-6 text-green-400"
          />
        );
      case 'error':
        return (
          <XCircleIcon
            aria-hidden="true"
            className="size-6 text-red-500"
          />
        );
      case 'warning':
        return (
          <ExclamationTriangleIcon
            aria-hidden="true"
            className="size-6 text-yellow-500"
          />
        );
      case 'info':
      default:
        return (
          <InformationCircleIcon
            aria-hidden="true"
            className="size-6 text-blue-500"
          />
        );
    }
  };

  return (
    <Transition
      show={t.visible}
      as={React.Fragment}
      enter="transform ease-out duration-300 transition"
      enterFrom="translate-y-2 opacity-0 sm:translate-y-0 sm:translate-x-2"
      enterTo="translate-y-0 opacity-100 sm:translate-x-0"
      leave="transition ease-in duration-100"
      leaveFrom="opacity-100"
      leaveTo="opacity-0"
    >
      <div className="pointer-events-auto w-full max-w-sm overflow-hidden rounded-lg bg-white shadow-lg ring-1 ring-black ring-opacity-5">
        <div className="p-4">
          <div className="flex items-start">
            <div className="shrink-0">{renderIcon()}</div>
            <div className="ml-3 w-0 flex-1 pt-0.5">
              <p className="text-sm font-medium text-gray-900">{title}</p>
              {message && (
                <p className="mt-1 text-sm text-gray-500">{message}</p>
              )}
            </div>
            <div className="ml-4 flex shrink-0">
              <button
                type="button"
                onClick={() => toast.dismiss(t.id)}
                className="inline-flex rounded-md bg-white text-gray-400 hover:text-gray-500 focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 focus:outline-none"
              >
                <span className="sr-only">Close</span>
                <SolidXMarkIcon aria-hidden="true" className="size-5" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </Transition>
  );
};

export default NotificationToast;